import React, { useState } from "react";
import {
  Button, Paper, Typography, Box, Alert, MenuItem,
  TextField, Table, TableHead, TableRow, TableCell, TableBody,
  FormControlLabel, Switch, Stepper, Step, StepLabel
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useStore } from "../../app/stores/store";
import LoadingComponent from "../../app/layout/LoadingComponent";
import { router } from "../../app/router/Routes";
import agent from "../../app/api/agent";

const MEMBER_FIELDS = [
  { key: "", label: "— Nicht zuordnen —" },
  { key: "firstName", label: "Vorname" },
  { key: "lastName", label: "Nachname" },
  { key: "fullName", label: "Vor- und Nachname (kombiniert)" },
  { key: "dateOfBirth", label: "Geburtsdatum" },
  { key: "placeOfBirth", label: "Geburtsort" },
  { key: "street", label: "Straße" },
  { key: "zipCode", label: "Postleitzahl" },
  { key: "city", label: "Stadt" },
  { key: "address", label: "Adresse (kombiniert)" },
  { key: "phone", label: "Telefon" },
  { key: "mobilePhone", label: "Mobiltelefon" },
  { key: "email", label: "E-Mail" },
  { key: "profession", label: "Beruf" },
  { key: "note", label: "Hinweis" },
  { key: "nationality", label: "Nationalität" },
  { key: "membershipFee", label: "Mitgliedsbeitrag" },
  { key: "paymentMethod", label: "Zahlungsmethode" },
  { key: "joinedAt", label: "Eintrittsdatum" },
  { key: "spouseName", label: "Ehepartner Name" },
  { key: "spouseProfession", label: "Beruf Ehepartner" },
  { key: "childrenNames", label: "Namen der Kinder" },
];

export default function UploadMembers() {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"info" | "error" | "success">("info");
  const memberStore = useStore((s) => s.memberStore);
  const { loading } = memberStore;

  // Step 0: file selection, Step 1: column mapping, Step 2: done
  const [step, setStep] = useState(0);
  const [headers, setHeaders] = useState<string[]>([]);
  const [columnMappings, setColumnMappings] = useState<Record<string, string>>({});
  const [useMapping, setUseMapping] = useState(false);
  const [startRow, setStartRow] = useState(2);
  const [sheetName, setSheetName] = useState("");
  const [headersLoading, setHeadersLoading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] || null;
    setFile(selected);
    setStep(0);
    setHeaders([]);
    setColumnMappings({});
    setMessage("");
  };

  const handleReadHeaders = async () => {
    if (!file) return;
    setHeadersLoading(true);
    setMessage("");
    try {
      const formData = new FormData();
      formData.append("xlsxFile", file);
      if (sheetName) formData.append("sheetName", sheetName);
      const result = await agent.Members.getXlsxHeaders(formData);
      setHeaders(result);
      setStep(1);
    } catch {
      setMessage(t("uploadMembers.errorMessage"));
      setMessageType("error");
    } finally {
      setHeadersLoading(false);
    }
  };

  const handleMappingChange = (memberField: string, excelColumn: string) => {
    setColumnMappings((prev) => {
      const next = { ...prev };
      if (!excelColumn) {
        delete next[memberField];
      } else {
        next[memberField] = excelColumn;
      }
      return next;
    });
  };

  const handleUpload = async () => {
    if (!file) {
      setMessage(t("uploadMembers.chooseFileLabel"));
      setMessageType("info");
      return;
    }

    const formData = new FormData();
    formData.append("xlsxFile", file);

    if (useMapping && Object.keys(columnMappings).length > 0) {
      formData.append("columnMappings", JSON.stringify(columnMappings));
      formData.append("startRow", startRow.toString());
      if (sheetName) formData.append("sheetName", sheetName);
    }

    try {
      await memberStore.uploadMembersXlsxData(formData);
      setMessage(t("uploadMembers.successMessage"));
      setMessageType("success");
      router.navigate("/members");
    } catch {
      setMessage(t("uploadMembers.errorMessage"));
      setMessageType("error");
    }
  };

  const handleDirectUpload = async () => {
    if (!file) {
      setMessage(t("uploadMembers.chooseFileLabel"));
      setMessageType("info");
      return;
    }

    const formData = new FormData();
    formData.append("xlsxFile", file);

    try {
      await memberStore.uploadMembersXlsxData(formData);
      setMessage(t("uploadMembers.successMessage"));
      setMessageType("success");
      router.navigate("/members");
    } catch {
      setMessage(t("uploadMembers.errorMessage"));
      setMessageType("error");
    }
  };

  const mappedFieldCount = Object.values(columnMappings).filter(Boolean).length;

  return (
    <Paper sx={{ p: 3, maxWidth: 800, mx: "auto", mt: 4 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t("uploadMembers.title")}
      </Typography>

      {(loading || headersLoading) && (
        <LoadingComponent content={t("uploadMembers.loadingMessage")} />
      )}

      {!loading && !headersLoading && (
        <>
          <Stepper activeStep={step} sx={{ mb: 3 }}>
            <Step><StepLabel>{t("uploadMembers.chooseFileLabel")}</StepLabel></Step>
            <Step><StepLabel>Spaltenzuordnung</StepLabel></Step>
          </Stepper>

          {/* Step 0: File selection */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t("uploadMembers.chooseFileLabel")}
            </Typography>
            <input type="file" accept=".xlsx" onChange={handleFileChange} />
          </Box>

          {file && step === 0 && (
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={useMapping}
                    onChange={(_, checked) => setUseMapping(checked)}
                  />
                }
                label="Spaltenzuordnung konfigurieren (für andere Excel-Formate)"
              />

              {useMapping ? (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    label="Blattname (optional)"
                    value={sheetName}
                    onChange={(e) => setSheetName(e.target.value)}
                    size="small"
                    sx={{ mr: 2, mb: 2 }}
                  />
                  <TextField
                    label="Startzeile"
                    type="number"
                    value={startRow}
                    onChange={(e) => setStartRow(parseInt(e.target.value) || 2)}
                    size="small"
                    sx={{ mb: 2 }}
                    slotProps={{ htmlInput: { min: 1 } }}
                  />
                  <br />
                  <Button
                    variant="outlined"
                    onClick={handleReadHeaders}
                    disabled={!file}
                  >
                    Spalten aus Datei lesen
                  </Button>
                </Box>
              ) : (
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleDirectUpload}
                    disabled={!file}
                    fullWidth
                  >
                    {t("uploadMembers.uploadButton")}
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {/* Step 1: Column mapping */}
          {step === 1 && headers.length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Ordnen Sie die Excel-Spalten den Mitgliederfeldern zu. ({mappedFieldCount} Felder zugeordnet)
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>Mitglieder-Feld</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Excel-Spalte</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {MEMBER_FIELDS.filter((f) => f.key).map((field) => (
                    <TableRow key={field.key}>
                      <TableCell>{field.label}</TableCell>
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={columnMappings[field.key] || ""}
                          onChange={(e) => handleMappingChange(field.key, e.target.value)}
                        >
                          <MenuItem value="">— Nicht zuordnen —</MenuItem>
                          {headers.map((h) => (
                            <MenuItem key={h} value={h}>{h}</MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                <Button variant="outlined" onClick={() => setStep(0)}>
                  Zurück
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUpload}
                  disabled={mappedFieldCount === 0}
                >
                  {t("uploadMembers.uploadButton")} ({mappedFieldCount} Felder)
                </Button>
              </Box>
            </Box>
          )}
        </>
      )}

      {message && (
        <Alert severity={messageType} sx={{ mt: 3 }}>
          {message}
        </Alert>
      )}
    </Paper>
  );
}
